/**
 * ProgressMeter — label + large count + horizontal fill bar + optional sub-label.
 * Used in Admin "Processing Pipeline" and Publisher "Distribution Channels".
 *
 * Flow: header row (label + count) → bar track → fill → optional sub-label.
 * The bar fills on mount via CSS transition.
 */
import { cn } from "@/shared/lib/utils";
import { useLocale } from "@/shared/hooks/useLocale";

interface ProgressMeterProps {
  label: string;
  count?: number;
  /** 0–100 fill percentage. */
  pct: number;
  /** Visual fill color — defaults to terracotta. */
  color?: string;
  sub?: string;
  /** Show count as large bold number (pipeline style). Defaults true. */
  bigCount?: boolean;
  className?: string;
}

export function ProgressMeter({
  label,
  count,
  pct,
  color = "var(--terracotta)",
  sub,
  bigCount = true,
  className,
}: ProgressMeterProps) {
  const { fmtNum } = useLocale();
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[12.5px] font-semibold" style={{ color: "var(--text-primary)" }}>
          {label}
        </span>
        {count !== undefined && (
          <span
            className={cn(bigCount ? "text-[21px] font-extrabold leading-none" : "text-[12px] font-bold")}
            style={{ color: "var(--text-primary)", letterSpacing: bigCount ? "-0.02em" : undefined, fontVariantNumeric: "tabular-nums" }}
          >
            {fmtNum(count)}
          </span>
        )}
      </div>
      <div className="h-[5px] rounded-full overflow-hidden" style={{ background: "var(--color-surface-low)" }}>
        <div
          className="h-full rounded-full transition-[width] duration-700"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      {sub && (
        <p className="text-[10.5px] mt-1" style={{ color: "var(--text-tertiary)" }}>
          {sub}
        </p>
      )}
    </div>
  );
}
